import { signupJambushrusti } from "@/services/jambushrustiAuth";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { supabase } from "@jambushrushti/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import {
  getAdminSession,
  loginAdmin,
  logoutAdmin,
} from "@jambushrushti/services/adminAuth";

export type UserRole =
  | "superadmin"
  | "admin"
  | "librarian"
  | "scholar"
  | "lms"
  | "user";

export const ROLE_LABELS: Record<UserRole, string> = {
  superadmin: "Super Admin",
  admin: "Admin",
  scholar: "Scholar",
  librarian: "Librarian",
  lms: "LMS (Learning)",
  user: "Registered User",
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  superadmin: "Full system access, manage admins & settings",
  admin: "CMS control, content management, approvals",
  scholar: "Research portal, submissions, access requests",
  librarian: "Library management system access",
  lms: "Learning portal access (courses, lectures, materials)",
  user: "Public site + bookmarks & profile",
};

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  avatar?: string;
}

interface AdminAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AdminUser | null;
  session: Session | null;
  login: (
    email: string,
    password: string,
    remember?: boolean
  ) => Promise<{
    success: boolean;
    redirectTo?: string;
    error?: string;
    role?: UserRole;
  }>;
  signup: (
    email: string,
    password: string,
    fullName: string
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  availableRoles: UserRole[];
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(
  undefined
);

export const AVAILABLE_ROLES: UserRole[] = [
  "superadmin",
  "admin",
  "scholar",
  "librarian",
  "lms",
  "user",
];

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  const fetchUserRole = async (userId: string): Promise<UserRole> => {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle();

    if (error || !data) {
      return "user";
    }

    return data.role as UserRole;
  };

  const fetchUserProfile = async (
    userId: string,
    email: string
  ): Promise<AdminUser> => {
    const { data: profile } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    const role = await fetchUserRole(userId);

    return {
      id: userId,
      email: email,
      name: profile?.full_name || email.split("@")[0],
      role,
      avatar: profile?.avatar_url,
    };
  };

  useEffect(() => {
    const adminSession = getAdminSession();
    if (adminSession) {
      const role = AVAILABLE_ROLES.includes(adminSession.user.role as UserRole)
        ? (adminSession.user.role as UserRole)
        : "admin";
      setUser({ ...adminSession.user, role });
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      setSession(currentSession);

      if (currentSession?.user) {
        setIsAuthenticated(true);
        // Defer fetching profile to avoid deadlock
        setTimeout(() => {
          fetchUserProfile(
            currentSession.user.id,
            currentSession.user.email || ""
          ).then(setUser);
        }, 0);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
      setIsLoading(false);
    });

    // THEN check for existing session
    supabase.auth
      .getSession()
      .then(({ data: { session: existingSession } }) => {
        setSession(existingSession);
        if (existingSession?.user) {
          setIsAuthenticated(true);
          fetchUserProfile(
            existingSession.user.id,
            existingSession.user.email || ""
          ).then(setUser);
        }
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
  }, []);

  const getRedirectPath = (role: UserRole): string => {
    switch (role) {
      case "superadmin":
      case "admin":
        return "/vault-cms-admin/dashboard";
      case "librarian":
        return "/lms/dashboard";
      case "scholar":
        return "/scholar/dashboard";
      case "lms":
        return "/learning/dashboard";
      case "user":
        return "/";
      default:
        return "/";
    }
  };

  const login = async (
    email: string,
    password: string,
    remember = false
  ): Promise<{
    success: boolean;
    redirectTo?: string;
    error?: string;
    role?: UserRole;
  }> => {
    try {
      const adminSession = await loginAdmin(email, password, remember);
      const role = AVAILABLE_ROLES.includes(adminSession.user.role as UserRole)
        ? (adminSession.user.role as UserRole)
        : "admin";
      const adminUser: AdminUser = { ...adminSession.user, role };
      setUser(adminUser);
      setIsAuthenticated(true);
      return {
        success: true,
        redirectTo: getRedirectPath(role),
        role,
      };
    } catch (error: any) {
      return { success: false, error: error.message || "An error occurred" };
    }
  };

  const signup = async (
    email: string,
    password: string,
    _fullName: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      await signupJambushrusti(email, password, "cms");
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || "An error occurred" };
    }
  };

  const logout = async () => {
    logoutAdmin();
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setUser(null);
    setSession(null);
  };

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!user) {
      return false;
    }
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  return (
    <AdminAuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        session,
        login,
        signup,
        logout,
        hasRole,
        availableRoles: AVAILABLE_ROLES,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
}
